import { useCallback, useEffect, useState } from "react";
import {
  Store, Megaphone, BarChart3, Loader2, Plug, CheckCircle2, XCircle, Plus,
  Trash2, KeyRound, ShieldCheck, ExternalLink, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Admin → Travel Commerce: provider registry, encrypted credential vault,
   TEST CONNECTION, affiliate application tracker, campaigns, revenue dashboard. */

const STATUS_STYLE = {
  deeplink: "bg-[#4285F4] text-white", production: "bg-[#34A853] text-white",
  sandbox: "bg-[#FBBC05] text-zinc-900", not_configured: "bg-zinc-200 text-zinc-700",
  disabled: "bg-zinc-400 text-white", error: "bg-[#EA4335] text-white",
};
const STATUS_LABEL = {
  deeplink: "Deep-link mode", production: "Production API", sandbox: "Sandbox",
  not_configured: "Not configured", disabled: "Disabled", error: "Error",
};
const APP_LABEL = {
  not_applied: "Not applied", started: "Application started", submitted: "Submitted",
  pending: "Pending approval", approved: "Approved", rejected: "Rejected", follow_up: "Needs follow-up",
};
const inp = "border border-zinc-300 px-2.5 py-1.5 text-sm bg-white w-full";

function ProviderCard({ p, statuses, appStatuses, reload }) {
  const [credOpen, setCredOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(p.last_test || null);
  const [saving, setSaving] = useState(false);

  const update = async (patch) => {
    try { await api.put(`/admin/travel/providers/${p.id}`, patch); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const saveCreds = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/travel/providers/${p.id}/credentials`, { values: vals });
      toast.success("Credentials saved (encrypted)");
      setVals({}); setCredOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/admin/travel/providers/${p.id}/test`);
      setTest(r.data);
      r.data.ok ? toast.success(`${p.name}: connection OK`) : toast.error(`${p.name}: test failed`);
    } catch { toast.error("Test failed"); }
    finally { setTesting(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete custom provider ${p.name}?`)) return;
    await api.delete(`/admin/travel/providers/${p.id}`);
    toast.success("Provider deleted"); reload();
  };

  return (
    <div className="bg-white border border-zinc-300 p-4" data-testid={`tc-provider-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-heading font-900">{p.name}</span>
        <span className={`text-[10px] font-700 px-2 py-0.5 ${STATUS_STYLE[p.status] || STATUS_STYLE.not_configured}`}
          data-testid={`tc-status-${p.id}`}>{STATUS_LABEL[p.status] || p.status}</span>
        {p.custom && <span className="text-[10px] font-700 px-2 py-0.5 bg-violet-100 text-violet-700">custom</span>}
        {p.credentials?.some((c) => c.set) && (
          <span className="text-[10px] font-700 px-2 py-0.5 bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> attribution ready
          </span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          <button onClick={runTest} disabled={testing} data-testid={`tc-test-${p.id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#4285F4] text-white text-xs font-700 hover:opacity-90 disabled:opacity-50">
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Test connection
          </button>
          {p.custom && <button onClick={del} className="p-1.5 text-zinc-400 hover:text-rose-600" data-testid={`tc-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
        </span>
      </div>

      {test && (
        <div className={`mt-2 text-xs px-2.5 py-1.5 border flex items-center gap-1.5 ${test.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-700"}`}>
          {test.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
          {test.ok ? "Link reachable" : `Failed${test.error ? ` (${test.error})` : test.http_status ? ` (HTTP ${test.http_status})` : ""}`}
          {test.api_check && ` · API: ${test.api_check}`}
          <a href={test.sample_url} target="_blank" rel="noopener noreferrer" className="underline ml-auto inline-flex items-center gap-1">sample link <ExternalLink className="w-3 h-3" /></a>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mt-3">
        <div>
          <label className="text-[10px] font-mono uppercase text-muted-foreground">Status</label>
          <select value={p.status} onChange={(e) => update({ status: e.target.value })} className={inp} data-testid={`tc-status-select-${p.id}`}>
            {statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase text-muted-foreground">Affiliate application</label>
          <select value={p.application?.status || "not_applied"}
            onChange={(e) => update({ application: { status: e.target.value } })} className={inp} data-testid={`tc-app-status-${p.id}`}>
            {appStatuses.map((s) => <option key={s} value={s}>{APP_LABEL[s] || s}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          {p.affiliate_program_url && (
            <a href={p.affiliate_program_url} target="_blank" rel="noopener noreferrer"
              className="text-xs text-primary underline inline-flex items-center gap-1">Apply to program <ExternalLink className="w-3 h-3" /></a>
          )}
        </div>
      </div>

      {(p.credential_fields || []).length > 0 && (
        <div className="mt-3">
          <button onClick={() => setCredOpen(!credOpen)} data-testid={`tc-creds-toggle-${p.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-700 text-zinc-700 hover:text-primary">
            <KeyRound className="w-3.5 h-3.5" /> Credentials ({p.credentials.filter((c) => c.set).length}/{p.credentials.length} set)
          </button>
          {credOpen && (
            <div className="mt-2 border border-zinc-200 bg-zinc-50 p-3 space-y-2">
              {p.credentials.map((c) => (
                <div key={c.key} className="grid sm:grid-cols-2 gap-2 items-center">
                  <div className="text-xs">
                    <span className="font-700">{c.label}</span>
                    {c.set && <span className={`ml-2 text-[10px] px-1.5 py-0.5 ${c.source === "env" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {c.source === "env" ? "from env secret" : "saved (encrypted)"}</span>}
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5">env: {c.env_var}</div>
                  </div>
                  <input type={c.secret ? "password" : "text"} value={vals[c.key] ?? ""}
                    onChange={(e) => setVals((v) => ({ ...v, [c.key]: e.target.value }))}
                    placeholder={c.set ? "•••••• (leave blank to keep)" : `Enter ${c.label}`}
                    className={inp} data-testid={`tc-cred-${p.id}-${c.key}`} />
                </div>
              ))}
              <div className="text-[10px] text-muted-foreground">Values are encrypted at rest and never sent back to the browser. Environment secrets (TC_*) override saved values.</div>
              <button onClick={saveCreds} disabled={saving || Object.keys(vals).length === 0} data-testid={`tc-creds-save-${p.id}`}
                className="px-3 py-1.5 bg-zinc-900 text-white text-xs font-700 disabled:opacity-40">
                {saving ? "Saving…" : "Save credentials"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddProvider({ reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", website: "", affiliate_program_url: "", deeplink_template: "", cred_keys: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    try {
      const credential_fields = f.cred_keys.split(",").map((s) => s.trim()).filter(Boolean)
        .map((k) => ({ key: k.toLowerCase().replace(/[^a-z0-9_]/g, ""), label: k, secret: false }));
      await api.post("/admin/travel/providers", { ...f, credential_fields });
      toast.success("Provider added"); setOpen(false);
      setF({ name: "", website: "", affiliate_program_url: "", deeplink_template: "", cred_keys: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add provider"); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="tc-add-provider"
      className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#34A853] text-white text-sm font-700 hover:opacity-90">
      <Plus className="w-4 h-4" /> Add provider
    </button>
  );
  return (
    <div className="bg-white border border-zinc-300 p-4 space-y-2" data-testid="tc-add-provider-form">
      <div className="font-700 text-sm">New provider (generic deep-link adapter)</div>
      <input value={f.name} onChange={set("name")} placeholder="Provider name (e.g. Hostelworld)" className={inp} data-testid="tc-new-name" />
      <input value={f.deeplink_template} onChange={set("deeplink_template")} className={inp} data-testid="tc-new-template"
        placeholder="URL template — use {destination} {checkin} {checkout} {adults} {children} {rooms}" />
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.website} onChange={set("website")} placeholder="Website (optional)" className={inp} />
        <input value={f.affiliate_program_url} onChange={set("affiliate_program_url")} placeholder="Affiliate program URL (optional)" className={inp} />
      </div>
      <input value={f.cred_keys} onChange={set("cred_keys")} className={inp} data-testid="tc-new-creds"
        placeholder="Affiliate URL params, comma-separated (e.g. aid, partner_id) — appended to links when set" />
      <div className="flex gap-2">
        <button onClick={save} disabled={!f.name || !f.deeplink_template.startsWith("http")} data-testid="tc-new-save"
          className="px-4 py-2 bg-zinc-900 text-white text-xs font-700 disabled:opacity-40">Add</button>
        <button onClick={() => setOpen(false)} className="px-4 py-2 text-xs text-muted-foreground">Cancel</button>
      </div>
    </div>
  );
}

function Campaigns() {
  const [rows, setRows] = useState(null);
  const [code, setCode] = useState("");
  const load = useCallback(() => api.get("/admin/travel/campaigns").then((r) => setRows(r.data.campaigns)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post("/admin/travel/campaigns", { code }); setCode(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (rows === null) return <Loader2 className="w-5 h-5 animate-spin" />;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 max-w-md">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="TP-NEW-CAMPAIGN" className={inp} data-testid="tc-campaign-input" />
        <button onClick={add} disabled={!code} className="px-4 py-1.5 bg-[#34A853] text-white text-xs font-700 disabled:opacity-40" data-testid="tc-campaign-add">Add</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.map((c) => (
          <span key={c.code} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 bg-white text-xs font-mono font-700" data-testid={`tc-campaign-${c.code}`}>
            {c.code}
            <button onClick={async () => { await api.delete(`/admin/travel/campaigns/${c.code}`); load(); }}
              className="text-zinc-400 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function Revenue() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/admin/travel/revenue?days=${days}`).then((r) => setD(r.data)).catch(() => setD({}));
  }, [days]);
  if (d === null) return <Loader2 className="w-5 h-5 animate-spin" />;
  const stat = (label, val, color) => (
    <div className="bg-white border border-zinc-300 p-4">
      <div className={`font-heading font-900 text-3xl ${color}`}>{val ?? 0}</div>
      <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="tc-revenue">
      <div className="flex items-center gap-2">
        {[7, 30, 90].map((n) => (
          <button key={n} onClick={() => setDays(n)}
            className={`px-3 py-1.5 text-xs font-700 border ${days === n ? "bg-[#4285F4] text-white border-[#4285F4]" : "border-zinc-300 bg-white"}`}>
            {n} days
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stat("Clicks", d.clicks, "text-[#4285F4]")}
        {stat("Outbound", d.outbound_clicks, "text-[#34A853]")}
        {stat("Reported bookings", d.user_reported_bookings, "text-[#FBBC05]")}
        {stat("Conversions", d.conversions, "text-[#EA4335]")}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-300 p-4">
          <div className="font-700 text-sm mb-2">By provider</div>
          {(d.by_provider || []).length === 0 ? <div className="text-xs text-muted-foreground">No clicks yet.</div> : (
            <table className="w-full text-xs">
              <thead><tr className="text-left text-muted-foreground"><th className="py-1">Provider</th><th>Clicks</th><th>Outbound</th><th>Booked</th></tr></thead>
              <tbody>{d.by_provider.map((r) => (
                <tr key={r.provider} className="border-t border-zinc-100">
                  <td className="py-1.5 font-700">{r.provider}</td><td>{r.clicks}</td><td>{r.outbound}</td><td>{r.booked}</td>
                </tr>))}</tbody>
            </table>
          )}
        </div>
        <div className="bg-white border border-zinc-300 p-4">
          <div className="font-700 text-sm mb-2">Top destinations</div>
          {(d.top_destinations || []).length === 0 ? <div className="text-xs text-muted-foreground">No data yet.</div> : (
            d.top_destinations.map((r) => (
              <div key={r.destination} className="flex justify-between text-xs py-1 border-t border-zinc-100 first:border-0">
                <span>{r.destination}</span><span className="font-700">{r.clicks}</span>
              </div>))
          )}
          <div className="font-700 text-sm mt-4 mb-2">By campaign</div>
          {(d.by_campaign || []).map((r) => (
            <div key={r.campaign} className="flex justify-between text-xs py-1 border-t border-zinc-100">
              <span className="font-mono">{r.campaign}</span><span className="font-700">{r.clicks}</span>
            </div>))}
        </div>
      </div>
    </div>
  );
}

export default function AdminTravelCommerce() {
  const [tab, setTab] = useState("providers");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/travel/providers").then((r) => setData(r.data)).catch(() => setData({ providers: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const TABS = [["providers", "Providers", Store], ["campaigns", "Campaigns", Megaphone], ["revenue", "Revenue", BarChart3]];
  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-[#EA4335] text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
          <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-3">
            <Store className="w-8 h-8 text-[#FBBC05]" /> Travel Commerce
          </h1>
          <p className="text-red-50 mt-2 text-sm">Booking providers, affiliate programs, tracked links and revenue — plug affiliate IDs in here or via TC_* environment secrets.</p>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex gap-2 mb-6">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`tc-tab-${id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 border ${tab === id ? "bg-[#4285F4] text-white border-[#4285F4]" : "bg-white border-zinc-300 text-zinc-700"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
          <button onClick={load} className="ml-auto p-2 text-zinc-400 hover:text-primary" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {tab === "providers" && (
          data === null ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <div className="space-y-4">
              <AddProvider reload={load} />
              {data.providers.map((p) => (
                <ProviderCard key={p.id} p={p} statuses={data.statuses || []} appStatuses={data.application_statuses || []} reload={load} />
              ))}
            </div>
          )
        )}
        {tab === "campaigns" && <Campaigns />}
        {tab === "revenue" && <Revenue />}
      </div>
    </div>
  );
}
